import { Controller } from "@hotwired/stimulus"

const MOBILE_BREAKPOINT = 768;
const DEFAULT_HERO_HEIGHT = 700;
const PRODUCTS_CONTAINER_SELECTOR = ".products-container";

export default class extends Controller {
  static get targets() { return ["heroImageContainer"]; }

  initialize() {
    // Pre-bound here rather than wired as a data-action because this listens on window, not on
    // this controller's own element tree -- a data-action cannot reach it.
    this.onWindowResize = this.onWindowResize.bind(this);
  }

  connect() {
    if (!this.hasHeroImageContainerTarget) { return; }

    // Wrapped in jQuery because every read/write against it (.data(), .width(), .css()) runs
    // inside the hero image's onload callback and the resize handler below, both of which only
    // ever run once the hero image actually finishes loading. The covering spec's hero image is
    // S3-backed and never resolves in this environment, so that path is unreachable here and a
    // native-DOM conversion cannot be proven safe by any spec in this suite.
    this.$heroImageContainer = $(this.heroImageContainerTarget);
    this.loadHeroImage();
    window.addEventListener("resize", this.onWindowResize);
  }

  disconnect() {
    if (!this.hasHeroImageContainerTarget) { return; }

    window.removeEventListener("resize", this.onWindowResize);
  }

  scrollToProducts() {
    // Stays jQuery: the covering spec replaces jQuery's own animate function to assert this
    // exact call is made (target elements, scrollTop, and the duration) -- a native scroll call
    // would be a different, unproven scroll mechanism and would fail that assertion outright.
    $("html, body").animate({
      scrollTop: $(PRODUCTS_CONTAINER_SELECTOR).offset().top
    }, 1000);
  }

  useDefaultHeroHeight() {
    return this.$heroImageContainer.data("use-default-height");
  }

  calculateDefaultHeroImagePositionY(heroImageNaturalHeight, heroImageRatio) {
    const scaledImageWidth = DEFAULT_HERO_HEIGHT * heroImageRatio;

    if (!this.useDefaultHeroHeight()) { return 0; }
    if (heroImageNaturalHeight <= DEFAULT_HERO_HEIGHT) { return 0; }
    if (scaledImageWidth >= this.$heroImageContainer.width()) { return 0; }

    return (DEFAULT_HERO_HEIGHT - (this.$heroImageContainer.width() / heroImageRatio)) / 2;
  }

  loadHeroImage() {
    const img = new Image();
    img.src = this.$heroImageContainer.css("background-image").replace(/"/g, "").replace(/url\(|\)$/ig, "");

    img.onload = () => {
      this.heroImageNaturalWidth = img.naturalWidth;
      this.heroImageNaturalHeight = img.naturalHeight;
      this.heroImageRatio = this.heroImageNaturalWidth / this.heroImageNaturalHeight;
      this.defaultHeroPositionY = this.calculateDefaultHeroImagePositionY(this.heroImageNaturalHeight, this.heroImageRatio);

      this.heroImageContainerTarget.style.aspectRatio = this.heroImageRatio;

      if (this.$heroImageContainer.data("aspect-ratio") == null) {
        this.$heroImageContainer.data("aspect-ratio", this.heroImageRatio);
      }

      this.resize(this.useDefaultHeroHeight(), this.defaultHeroPositionY);
    };
  }

  onWindowResize() {
    this.defaultHeroPositionY = this.calculateDefaultHeroImagePositionY(this.heroImageNaturalHeight, this.heroImageRatio);
    this.resize(this.useDefaultHeroHeight(), this.defaultHeroPositionY);
  }

  resize(useDefaultHeroHeight, defaultHeroPositionY) {
    const width = this.$heroImageContainer.width();
    const aspectRatio = parseFloat(this.$heroImageContainer.data("aspect-ratio"));

    let offsetRatio = parseFloat(this.$heroImageContainer.data("offset-ratio"));
    let mobileOffsetRatio = parseFloat(this.$heroImageContainer.data("mobile-offset-ratio"));

    offsetRatio = Number.isNaN(offsetRatio) ? false : offsetRatio;
    mobileOffsetRatio = Number.isNaN(mobileOffsetRatio) ? 0 : mobileOffsetRatio;

    const useAspectRatio = (width >= MOBILE_BREAKPOINT) ? aspectRatio : 1; // we are not resizing mobile hero atm
    const useOffsetRatio = (width >= MOBILE_BREAKPOINT) ? offsetRatio : mobileOffsetRatio;

    const maxHeight = (useDefaultHeroHeight ? DEFAULT_HERO_HEIGHT : parseInt(width * useAspectRatio));
    const backgroundPositionY = parseInt(maxHeight * useOffsetRatio);

    if (offsetRatio !== false) {
      this.$heroImageContainer.css("background-position-y", `${backgroundPositionY}px`);
    } else {
      this.$heroImageContainer.css("background-position-y", `${defaultHeroPositionY}px`);
    }

    this.$heroImageContainer.css("max-height", `${maxHeight}px`);
  }
}
